import { cn } from '~/utils/cn';

import type { ChatbotComparisonRow, StatusTone } from '../../../types/chatbot-message.types';

import { StatusBadge } from './status-badge';

const TREND_CLASSES = {
  up: 'text-success',
  down: 'text-error',
  neutral: 'text-foreground',
} as const;

const TREND_GLYPH = {
  up: '↑',
  down: '↓',
  neutral: '',
} as const;

interface ComparisonCardProps {
  tone?: StatusTone | undefined;
  badge?: string | undefined;
  columns: { left: string; right: string; subLeft?: string; subRight?: string };
  rows: ChatbotComparisonRow[];
}

/** Two-column comparison table — used for the Day 28 results readout (Arm A control vs Arm B variant). */
export function ComparisonCard({ tone, badge, columns, rows }: ComparisonCardProps) {
  return (
    <div className="bg-muted/40 space-y-3 rounded-xl p-3">
      {tone && badge ? <StatusBadge tone={tone}>{badge}</StatusBadge> : null}
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-2 text-xs">
        {/* Column headers — first cell empty, then left + right column titles. */}
        <span />
        <div className="text-right">
          <p className="text-foreground font-medium">{columns.left}</p>
          {columns.subLeft ? (
            <p className="text-muted-foreground text-[11px]">{columns.subLeft}</p>
          ) : null}
        </div>
        <div className="text-right">
          <p className="text-foreground font-medium">{columns.right}</p>
          {columns.subRight ? (
            <p className="text-muted-foreground text-[11px]">{columns.subRight}</p>
          ) : null}
        </div>

        {rows.map((row) => {
          const trend = row.rightTrend ?? 'neutral';
          return (
            <span key={row.label} className="contents">
              <p className="text-foreground font-medium">{row.label}</p>
              <p className="text-muted-foreground text-right tabular-nums">{row.left}</p>
              <p className={cn('text-right font-semibold tabular-nums', TREND_CLASSES[trend])}>
                {row.right}
                {TREND_GLYPH[trend] ? (
                  <span className="ml-1" aria-hidden="true">
                    {TREND_GLYPH[trend]}
                  </span>
                ) : null}
              </p>
            </span>
          );
        })}
      </div>
    </div>
  );
}
